import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, HelpCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import Button from "./Button";

/** How serious the answer is, which decides the icon and the confirm button. */
export type ConfirmTone = "danger" | "warning" | "neutral";

export interface ConfirmRequest {
  title: string;
  /** The consequence, in a sentence or two. What `window.confirm` had no room for. */
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /**
   * How wide the card may get, as a Tailwind max-width class.
   *
   * Exists for the driver screens, which are capped at phone width — a 512px
   * dialog rising over a 448px column reads as belonging to a different app,
   * and on a laptop the mismatch is plain. Everywhere else keeps the default,
   * because a desk dialog has the room and the sentences to use it.
   */
  maxWidthClass?: string;
}

const TONES: Record<
  ConfirmTone,
  { icon: typeof AlertTriangle; iconClass: string; variant: "danger" | "primary" }
> = {
  danger: {
    icon: Trash2,
    iconClass: "bg-[#FCE9E9] text-[#A63A39]",
    variant: "danger",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "bg-[#FCEEE2] text-[#8A5A28]",
    variant: "primary",
  },
  neutral: {
    icon: HelpCircle,
    iconClass: "bg-bone text-mist",
    variant: "primary",
  },
};

interface ConfirmDialogProps extends ConfirmRequest {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The question asked before something irreversible happens.
 *
 * Replaces `window.confirm` throughout the container module. The browser's own
 * dialog takes one string, so every prompt had to be a single run-on sentence
 * carrying the action, the consequence and the caveat at once — and it renders
 * in the OS's chrome, at the top of the screen, in the OS's language, with a
 * button pair the user cannot be given better names for than "OK" and "Cancel".
 * On a phone it is worse: a system sheet the driver has to hunt for while
 * holding the container's paperwork.
 *
 * This says the same thing in three parts — what is about to happen, what it
 * costs, and two buttons that name their own actions — and it is the page's own
 * furniture, so it reads as part of the app on any width.
 *
 * Portalled to `document.body` because most call sites sit inside a `<form>` or
 * under a sticky header: as a child it would inherit a stacking context that can
 * trap it behind the header, and its buttons would be inside the form. They are
 * `type="button"` besides, so neither can submit anything by accident.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  maxWidthClass = "max-w-lg",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation("common");

  // Escape answers "no" — the safe answer, and the one a stray key should give.
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCancel();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
    };
  }, [open, onCancel]);

  if (!open) return null;

  const { icon: Icon, iconClass, variant } = TONES[tone];

  return createPortal(
    <div
      className="fixed inset-0 z-60 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      // Clicking the backdrop is a refusal, like Escape. Guarded on the target
      // so a drag that ends outside the card does not count as one.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={description ? "confirm-dialog-description" : undefined}
        // Full-width sheet on a phone, centred card from `sm` up: the buttons
        // stay under the thumb on the screen where that matters.
        className={`w-full ${maxWidthClass} rounded-t-2xl bg-white p-4 shadow-2xl sm:rounded-2xl sm:p-6`}
      >
        <div className="flex gap-3 sm:gap-4">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconClass}`}
            aria-hidden
          >
            <Icon className="h-5 w-5" />
          </span>

          <div className="min-w-0 flex-1">
            <h2
              id="confirm-dialog-title"
              className="text-[15px] font-semibold text-ink sm:text-base"
            >
              {title}
            </h2>
            {description && (
              <div
                id="confirm-dialog-description"
                className="mt-1 text-[13px] leading-relaxed text-mist"
              >
                {description}
              </div>
            )}
          </div>
        </div>

        {/* Reversed on a phone so the confirming action sits lowest — nearest
            the thumb — while still reading Cancel-then-Confirm left to right on
            a wide screen. */}
        <div className="mt-4 flex flex-col-reverse gap-2 sm:mt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel ?? t("buttons.cancel")}
          </Button>
          {/* Focus lands here: it is the reason the dialog opened, and Escape
              (or Tab to Cancel) is always one key away from refusing. */}
          <Button type="button" variant={variant} onClick={onConfirm} autoFocus>
            {confirmLabel ?? t("buttons.confirm")}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ConfirmDialog;
